import { BorderRadius, Color, Spacing, Typography } from '@persona-id/design-tokens';
import {
  ArrowCollapseIcon,
  ArrowExpandIcon,
  BranchIcon,
  ClockIcon,
  GridIcon,
  ParallelIcon,
  PersonaIcon,
  RotateCwIcon,
  ZapIcon,
} from '@persona-id/design-tokens/icons';
import { PDSIconComponent } from '@persona-id/design-tokens/icons/types';
import { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import {
  NODE_WIDTH,
  WorkflowCanvasCustomEvent,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/constants';
import {
  stepDecorationsFor,
  WorkflowRunStepDisplay,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/utils';
import { BaseNodeProps } from 'dashboard/components-lib/FlowPlotter';
import { WorkflowRunStepStatsCard } from 'dashboard/components-lib/WorkflowRunStepStats';
import { humanReadableRunTime, startEndTime } from 'dashboard/lib/workflows/run-stats';
import { WorkflowRunStep } from 'dashboard/models/workflow-step';
import { FakeWorkflowStepType, WorkflowStepType } from 'dashboard/models/workflow-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import { HStack } from 'design-system/components/Stack';
import Tooltip from 'design-system/components/Tooltip';
import { Elevation } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';

const OuterContainer = styled.div.attrs<{
  borderColor: Color;
  borderDashed: boolean;
  filter: string;
  focused: boolean;
  selected: boolean;
  tilted: boolean;
  title: string;
}>(({ borderColor, filter, selected, tilted, title }) => ({
  'aria-current': selected,
  role: 'button',
  'aria-label': title,
  title,
  style: {
    borderColor,
    rotate: tilted ? '4deg' : 'none',
    filter,
  },
}))<{
  borderColor: Color;
  borderDashed: boolean;
  filter: string;
  focused: boolean;
  selected: boolean;
  tilted: boolean;
}>`
  width: ${NODE_WIDTH}px;
  height: 68px;
  display: flex;
  position: relative;
  align-items: center;
  padding: ${Spacing[3]};
  background-color: ${({ selected, focused }) =>
    !selected && focused ? Color.CorePurple100 : Color.PrimaryWhite};
  box-shadow: ${Elevation.Mid};
  border-style: ${({ borderDashed }) => (borderDashed ? 'dashed' : 'solid')};
  border-width: 2px;
  border-radius: ${BorderRadius.Lg};
  cursor: pointer;
  user-select: none;
  z-index: ${Z_INDICES.withShadow};

  transition-property: border-color;
  transition-duration: 0.25s;
  transition-timing-function: ease-out;
`;

const LeftIcon = styled.div<{ deactivated: boolean; collapsed: boolean | null }>`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: ${BorderRadius.Md};
  background-color: ${({ deactivated }) => (deactivated ? Color.CoreSlate50 : Color.CorePurple100)};
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ collapsed }) => {
    if (collapsed == null) {
      return;
    }
    const collapsedStyling = `
      & > .node-step-card__icon-default {
        display: none;
      }

      & > .node-step-card__icon-hovered {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      background-color: ${Color.CorePurple600};

      &:hover {
        background-color: ${Color.CorePurple800};
      }
    `;
    const expandedStyling = `
      & > .node-step-card__icon-hovered {
        display: none;
      }

      &:hover {
        background-color: ${Color.CorePurple800};

        & > .node-step-card__icon-default {
          display: none;
        }

        & > .node-step-card__icon-hovered {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    `;
    return `
      transition: background-color ${TransitionDuration.Fast};

      ${collapsed ? collapsedStyling : expandedStyling}
    `;
  }}
`;

const CenterText = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-left: ${Spacing[3]};
  overflow: hidden;
`;

const StepTitle = styled.div`
  ${Typography.TextMediumBold}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StepSubTitle = styled.div`
  ${Typography.TextSmall}
  color: ${Color.TextSecondary};
`;

const StepDuration = styled.div`
  ${Typography.TextSmall}
  color: ${Color.TextSecondary};
  :before {
    content: '•';
    margin-right: ${Spacing[1]};
  }
`;

const CIRCLE_DIAMETER = 10;
const Circle = styled.div<{ color: Color }>`
  position: absolute;
  border-radius: 50%;
  width: ${CIRCLE_DIAMETER}px;
  height: ${CIRCLE_DIAMETER}px;
  left: ${(NODE_WIDTH - CIRCLE_DIAMETER) / 2 - 2}px;
  background-color: ${({ color }) => color};

  transition-property: background-color;
  transition-duration: 0.25s;
  transition-timing-function: ease-out;
`;
const TopCircle = styled(Circle)`
  top: ${-(CIRCLE_DIAMETER + 2) / 2}px;
`;
const BottomCircle = styled(Circle)`
  bottom: ${-(CIRCLE_DIAMETER + 2) / 2}px;
`;

const MaybeWithStats: FC<PropsWithChildren<{ runStep: WorkflowRunStep | null; name: string }>> = ({
  name,
  children,
  runStep,
}) => {
  return runStep != null ? (
    // Need to surround with div instead of React.Fragment to bubble up hover event for radix
    <Tooltip trigger={<div>{children}</div>}>
      <WorkflowRunStepStatsCard runStep={runStep} stepName={name} />
    </Tooltip>
  ) : (
    <>{children}</>
  );
};

interface NodeStepProps {
  collapsed: boolean | null;
  connectors: 'top' | 'bottom' | 'top-and-bottom';
  deactivated: boolean;
  errored: boolean;
  focused: boolean;
  icon: PDSIconComponent | null;
  name: string;
  borderDashed?: boolean;
  run: WorkflowRunStepDisplay | null;
  selected: boolean;
  typeName: string;
}

const NodeStep: FC<NodeStepProps & BaseNodeProps> = ({
  borderDashed = false,
  collapsed,
  connectors,
  deactivated,
  dragged,
  dragging,
  errored,
  focused,
  icon: Icon,
  name,
  typeName,
  run,
  selected,
  surrounded,
  onNodeCustomEvent,
}) => {
  const [status, durationString, runStep] = useMemo(() => {
    const runStatus = run?.status ?? null;

    if (run?.runStep) {
      const { startTime, endTime } = startEndTime([run.runStep]);
      return [runStatus, humanReadableRunTime(startTime, endTime).durationString, run.runStep];
    } else {
      return [runStatus, null, null];
    }
  }, [run]);

  const selectNode = () => {
    if (!selected) {
      onNodeCustomEvent({ key: WorkflowCanvasCustomEvent.Select });
    }
  };

  const { accentColor, filterEffect } = stepDecorationsFor({
    deactivated,
    dragged,
    errored,
    selected,
    status,
    surrounded,
  });

  return (
    <MaybeWithStats name={name} runStep={runStep}>
      <OuterContainer
        borderColor={accentColor}
        borderDashed={borderDashed}
        filter={filterEffect}
        focused={focused}
        selected={selected}
        tilted={dragging}
        title={name}
        onClick={selectNode}
      >
        {['top', 'top-and-bottom'].includes(connectors) && <TopCircle color={accentColor} />}
        {['bottom', 'top-and-bottom'].includes(connectors) && <BottomCircle color={accentColor} />}
        <LeftIcon collapsed={collapsed} deactivated={deactivated}>
          {Icon && (
            <Icon
              className='node-step-card__icon-default'
              color={
                deactivated
                  ? selected
                    ? Color.BorderIconPrimary
                    : Color.BorderIconSecondary
                  : selected
                    ? Color.CorePurple800
                    : Color.CorePurple600
              }
              role='img'
              size={24}
            />
          )}
          {collapsed != null && (
            <div
              aria-label={collapsed ? 'Show nested steps' : 'Hide nested steps'}
              className='node-step-card__icon-hovered'
              role='button'
              onClick={(e) => {
                onNodeCustomEvent({ key: WorkflowCanvasCustomEvent.ToggleCollapsed });
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {collapsed ? (
                <ArrowExpandIcon color={Color.PrimaryWhite} size='24' />
              ) : (
                <ArrowCollapseIcon color={Color.PrimaryWhite} size='24' />
              )}
            </div>
          )}
        </LeftIcon>
        <CenterText>
          <StepTitle>{typeName !== '' && name}</StepTitle>
          <HStack spacing={Spacing[1]}>
            <StepSubTitle>{typeName}</StepSubTitle>
            {durationString && <StepDuration>{durationString}</StepDuration>}
          </HStack>
        </CenterText>
      </OuterContainer>
    </MaybeWithStats>
  );
};

export const NodeStepInput: FC<
  Pick<NodeStepProps, 'errored' | 'focused' | 'name' | 'selected' | 'run' | 'typeName'> &
    BaseNodeProps
> = (props) => {
  return (
    <NodeStep
      {...props}
      deactivated={false}
      surrounded={false}
      icon={PersonaIcon}
      collapsed={null}
      connectors='bottom'
    />
  );
};

export const NodeStepOutput: FC<
  Pick<NodeStepProps, 'deactivated' | 'errored' | 'focused' | 'name' | 'selected' | 'run'> &
    BaseNodeProps
> = (props) => {
  return (
    <NodeStep
      {...props}
      icon={PersonaIcon}
      collapsed={null}
      surrounded={false}
      connectors='top'
      typeName='Output'
    />
  );
};

const StackedCard = styled.div<{
  index: number;
  color: Color;
  filter: string;
  tilted: boolean;
}>`
  top: ${({ index }) => 34 + (index + 1) * 5}px;
  position: absolute;
  width: ${NODE_WIDTH}px;
  height: 34px;
  box-shadow: ${({ index }) => (index === 1 ? Elevation.Mid : 'none')};
  border-width: 2px;
  border-top-width: 0;
  border-style: solid;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-color: ${({ color }) => color};
  filter: ${({ filter }) => filter};
  rotate: ${({ tilted }) => (tilted ? '4deg' : 'none')};

  transition-property: border-color;
  transition-duration: 0.25s;
  transition-timing-function: ease-out;
`;

const TYPES_HAS_BRANCH = {
  [WorkflowStepType.Conditional]: ['Conditional', BranchIcon],
  [WorkflowStepType.Loop]: ['Loop', RotateCwIcon],
  [WorkflowStepType.Module]: ['Module', GridIcon],
  [WorkflowStepType.Parallel]: ['Parallel', ParallelIcon],
} as const;

export const NodeStepWithBranch: FC<
  {
    type: keyof typeof TYPES_HAS_BRANCH;
    connectors?: NodeStepProps['connectors'];
    typeName?: NodeStepProps['typeName'];
  } & Pick<NodeStepProps, 'collapsed' | 'errored' | 'focused' | 'name' | 'selected' | 'run'> &
    BaseNodeProps
> = ({
  collapsed,
  connectors = 'top-and-bottom',
  dragged,
  dragging,
  errored,
  run,
  selected,
  surrounded,
  type,
  typeName,
  onNodeCustomEvent,
  ...restProps
}) => {
  useEffect(() => {
    if (dragged && !collapsed) {
      onNodeCustomEvent({ key: WorkflowCanvasCustomEvent.ToggleCollapsed });
    }
  }, [dragged, collapsed, onNodeCustomEvent]);

  const { accentColor, filterEffect } = stepDecorationsFor({
    deactivated: false,
    dragged,
    errored,
    selected,
    status: run?.status ?? null,
    surrounded,
  });

  return (
    <>
      <NodeStep
        {...restProps}
        deactivated={false}
        dragged={dragged}
        dragging={dragging}
        errored={errored}
        run={run}
        selected={selected}
        surrounded={surrounded}
        collapsed={collapsed}
        connectors={connectors}
        icon={TYPES_HAS_BRANCH[type][1]}
        typeName={typeName ?? TYPES_HAS_BRANCH[type][0]}
        onNodeCustomEvent={onNodeCustomEvent}
      />{' '}
      {collapsed && (
        <>
          <StackedCard index={0} color={accentColor} filter={filterEffect} tilted={dragging} />
          <StackedCard index={1} color={accentColor} filter={filterEffect} tilted={dragging} />
        </>
      )}
    </>
  );
};

const TYPES_NO_BRANCH = {
  [WorkflowStepType.Action]: ['Action', ZapIcon],
  [WorkflowStepType.Wait]: ['Wait', ClockIcon],
  [WorkflowStepType.Module]: ['Module', GridIcon],
  [FakeWorkflowStepType.MapOutput]: ['Map Output', RotateCwIcon],
} as const;

export const NodeStepWithoutBranch: FC<
  { type: keyof typeof TYPES_NO_BRANCH } & Pick<
    NodeStepProps,
    'deactivated' | 'errored' | 'focused' | 'name' | 'selected' | 'run'
  > &
    BaseNodeProps
> = ({ type, ...restProps }) => {
  return (
    <NodeStep
      {...restProps}
      collapsed={null}
      connectors='top-and-bottom'
      icon={TYPES_NO_BRANCH[type][1]}
      typeName={TYPES_NO_BRANCH[type][0]}
    />
  );
};

export const NodeStepModule: FC<
  { expandable: boolean } & Pick<
    NodeStepProps,
    'name' | 'focused' | 'collapsed' | 'errored' | 'selected' | 'run'
  > &
    BaseNodeProps
> = ({ expandable, collapsed, ...restProps }) => {
  return expandable ? (
    <NodeStepWithBranch
      {...restProps}
      type={WorkflowStepType.Module}
      collapsed={collapsed}
      connectors={collapsed ? 'top-and-bottom' : 'bottom'}
      typeName='Module'
    />
  ) : (
    <NodeStepWithoutBranch {...restProps} type={WorkflowStepType.Module} deactivated={false} />
  );
};

export const NodeStepPlaceholder: FC<
  Pick<NodeStepProps, 'errored' | 'selected'> & BaseNodeProps
> = (props) => {
  return (
    <NodeStep
      {...props}
      borderDashed
      icon={null}
      collapsed={null}
      connectors='top-and-bottom'
      deactivated={false}
      name='Select step type'
      run={null}
      typeName=''
      focused={false}
    />
  );
};
